import * as React from 'react';
import { useState } from 'react';

/** Material UI Packages. */
import { Box, Button, Popover, Typography } from '@mui/material';

/** Material UI Icons. */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

/** Internal Components. */
import ThemeButton from 'shared/ui/Button';
import { formatFilterButtonLabel } from 'shared/lib/filterLabel';

/** Filter button look: filled once a value is set, red border when a required filter is missing. */
export const filterButtonSx = (active, invalid = false) => ({
    height: '26px',
    fontSize: '12px',
    textTransform: 'none',
    borderRadius: '6px',
    px: 1.25,
    whiteSpace: 'nowrap',
    color: active ? 'white' : '#00647e',
    bgcolor: active ? '#00647e' : 'white',
    borderColor: invalid ? '#d32f2f' : '#00647e',
    borderWidth: invalid ? '1.5px' : '1px',
    '&:hover': {
        borderColor: invalid ? '#d32f2f' : '#003a52',
        borderWidth: invalid ? '1.5px' : '1px',
        bgcolor: active ? '#003a52' : 'rgba(0, 100, 126, 0.06)',
    },
});

/**
 * One filter in the filter bar: a button that opens a popover where the operator picks a value.
 * The pick takes effect on APPLY, or when the popover is closed, so it is not lost when the
 * operator skips APPLY. While `error` is set nothing takes effect — APPLY is disabled and
 * closing drops the pick. RESET only clears the pick in the popover.
 *
 * @param {string} label - Filter name shown on the button.
 * @param {string|null} title - Popover title line; defaults to `label`, null when the body brings its own.
 * @param {*} value - Applied value (null = nothing applied).
 * @param {string} valueText - Applied value as the button shows it.
 * @param {function} onApply - Called with the draft when APPLY is pressed or the popover closes.
 * @param {*} resetValue - What RESET puts in the draft (default null).
 * @param {function} children - `(draft, setDraft) => node`, the popover body.
 * @param {number|string} width - Popover width.
 * @param {string|function} error - Why the draft cannot be applied: a string, or `(draft) => string|null`
 *   evaluated against the live draft. Shown under the body in red; APPLY is disabled and closing
 *   does not apply while set.
 * @param {boolean} invalid - Draws the button with a red border (a required filter the page rejected).
 * @param {node} notice - Always-visible line under the title: the mode this filter is in and why.
 * @param {node} blocked - When set, replaces the body and the RESET/APPLY row: the filter cannot be
 *   used right now, and this content says why and how to unblock it (links that change other filters).
 */
export default function FilterButtonPopover({
    label,
    title = label,
    value,
    valueText,
    onApply,
    resetValue = null,
    children,
    width = 300,
    error = null,
    invalid = false,
    disabled = false,
    notice = null,
    blocked = null,
}) {
    const [anchor, setAnchor] = useState(null);
    const [draft, setDraft] = useState(value);

    const open = (event) => {
        setDraft(value);
        setAnchor(event.currentTarget);
    };
    const close = () => setAnchor(null);
    const apply = () => {
        onApply(draft);
        close();
    };
    const errorText = typeof error === 'function' ? error(draft) : error;
    /** Closing without APPLY still applies the draft, unless it is invalid or the filter is blocked. */
    const closeAndApply = () => (errorText || blocked ? close() : apply());

    const active = Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';

    return (
        <>
            <Button
                variant="outlined"
                size="small"
                onClick={open}
                disabled={disabled}
                endIcon={<ArrowDropDownIcon sx={{ fontSize: '18px !important', ml: -0.5 }} />}
                sx={filterButtonSx(active, invalid)}
                aria-invalid={invalid || undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(anchor)}
            >
                {formatFilterButtonLabel(label, active ? valueText : null)}
            </Button>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={closeAndApply}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ p: '12px 15px', display: 'flex', flexDirection: 'column', gap: 1.25, width }}>
                    {title ? <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#003a52' }}>{title}</Typography> : null}
                    {notice && !blocked && <Box sx={{ fontSize: 11, color: '#5a6b74', mt: -0.5 }}>{notice}</Box>}
                    {blocked ? (
                        <Box sx={{ fontSize: 12, color: '#003a52' }}>{blocked}</Box>
                    ) : (
                        <>
                            {children(draft, setDraft)}
                            {errorText && <Typography sx={{ fontSize: 11, color: '#d32f2f' }}>{errorText}</Typography>}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <ThemeButton
                                    text="RESET"
                                    width={'84px'}
                                    onClick={() => setDraft(resetValue)}
                                    sx={{ border: '#00647e', bgcolor: 'white', color: '#00647e' }}
                                />
                                <ThemeButton text="APPLY" width={'84px'} onClick={apply} disabled={Boolean(errorText)} />
                            </Box>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    );
}
